import { Button } from "@chakra-ui/button";
import { FormControl, FormLabel } from "@chakra-ui/form-control";
import { Input, InputGroup, InputRightElement } from "@chakra-ui/input";
import { VStack, Text, HStack } from "@chakra-ui/layout";
import { useToast } from "@chakra-ui/toast";
import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";

const RESEND_COOLDOWN = 60;

const strengthOf = (pw) => {
  if (!pw) return { pct: 0, label: "", color: "transparent" };
  let score = 0;
  if (pw.length >= 6) score += 1;
  if (pw.length >= 10) score += 1;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  if (score <= 2) return { pct: 33, label: "Weak — go harder", color: "#f87171" };
  if (score <= 3) return { pct: 62, label: "Solid — almost elite", color: "#fbbf24" };
  return { pct: 100, label: "Beast mode 🔥", color: "#34d399" };
};

// Forgot-password flow mirroring VerifyOtp (email → 6-digit code → new
// password). Backend: POST /forgot-password (send) →
// POST /verify-reset-otp (verify, issues resetToken) →
// POST /reset-password (resetToken + newPassword).
const ForgotPassword = ({ initialEmail = "", onBack, onDone }) => {
  const toast = useToast();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState(initialEmail || "");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [preview, setPreview] = useState("");
  const otpRef = useRef(null);

  const strength = useMemo(() => strengthOf(newPassword), [newPassword]);
  const matchState =
    !confirm || !newPassword
      ? null
      : newPassword === confirm
      ? { ok: true, msg: "Passwords match ✓" }
      : { ok: false, msg: "Passwords don't match yet" };

  useEffect(() => {
    setEmail(initialEmail || "");
  }, [initialEmail]);

  useEffect(() => {
    if (step === "otp" && otpRef.current) otpRef.current.focus();
  }, [step]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(
      () => setCooldown((c) => (c > 0 ? c - 1 : 0)),
      1000
    );
    return () => clearInterval(id);
  }, [cooldown]);

  const sendCode = async (e) => {
    if (e) e.preventDefault();
    const trimmed = (email || "").trim();
    if (!trimmed) {
      toast({
        title: "Enter your email address",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post("/api/user/forgot-password", {
        email: trimmed,
      });
      setEmail(data.email || trimmed.toLowerCase());
      setPreview(data.devPreviewOtp || "");
      setOtp("");
      setStep("otp");
      setCooldown(RESEND_COOLDOWN);
      toast({
        title:
          data.emailSent === false ? "Code ready (email hiccup)" : "Check your email",
        description:
          data.emailSent === false
            ? "We couldn't send the email. Tap Resend for a fresh code."
            : `We sent a 6-digit reset code to ${data.email || trimmed}.`,
        status: data.emailSent === false ? "warning" : "success",
        duration: 6000,
        isClosable: true,
        position: "bottom",
      });
    } catch (error) {
      const payload = error.response?.data || {};
      if (payload.retryAfter) setCooldown(payload.retryAfter);
      // A fresh code may still be cooling down — jump to the OTP step so
      // the user can enter the code they already received.
      if (payload.code === "OTP_RATE_LIMITED") {
        setEmail(payload.email || trimmed.toLowerCase());
        setStep("otp");
      }
      toast({
        title: "Couldn't send reset code",
        description: payload.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    try {
      const { data } = await axios.post("/api/user/forgot-password", {
        email,
      });
      if (data.devPreviewOtp) setPreview(data.devPreviewOtp);
      else setPreview("");
      toast({
        title: data.emailSent === false ? "Code regenerated" : "New code sent",
        description:
          data.emailSent === false
            ? "Email service hiccup — use the new code below (dev) or try again."
            : `Check ${email} for the fresh 6-digit code.`,
        status: data.emailSent === false ? "warning" : "success",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      setCooldown(RESEND_COOLDOWN);
    } catch (error) {
      const payload = error.response?.data || {};
      if (payload.retryAfter) setCooldown(payload.retryAfter);
      toast({
        title: "Couldn't resend yet",
        description: payload.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    } finally {
      setResending(false);
    }
  };

  const verify = async (e) => {
    if (e) e.preventDefault();
    if (otp.length !== 6) {
      toast({
        title: "Enter the 6-digit code",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post("/api/user/verify-reset-otp", {
        email,
        otp,
      });
      setResetToken(data.resetToken || "");
      setStep("password");
      toast({
        title: "Code verified ✓",
        description: "Now set your new password.",
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
    } catch (error) {
      const payload = error.response?.data || {};
      toast({
        title: "Verification failed",
        description: payload.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      if (payload.code === "OTP_EXPIRED" || payload.code === "OTP_LOCKED") {
        setCooldown((c) => (c > 0 ? c : 0));
      }
    } finally {
      setLoading(false);
    }
  };

  const reset = async (e) => {
    if (e) e.preventDefault();
    if (!newPassword || !confirm) {
      toast({
        title: "Please fill both password fields",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }
    if (newPassword !== confirm) {
      toast({
        title: "Passwords Do Not Match",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post("/api/user/reset-password", {
        resetToken,
        newPassword,
      });
      toast({
        title: "Password reset ✓",
        description: data.message || "Log in with your new password.",
        status: "success",
        duration: 6000,
        isClosable: true,
        position: "bottom",
      });
      setStep("done");
      if (onDone) onDone(data);
    } catch (error) {
      const payload = error.response?.data || {};
      toast({
        title: "Reset failed",
        description: payload.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      // Expired reset session → back to the code step for a fresh verify.
      if (payload.code === "RESET_INVALID_TOKEN") {
        setStep("otp");
        setResetToken("");
      }
    } finally {
      setLoading(false);
    }
  };

  const backLink = (label) =>
    onBack ? (
      <Button variant="link" size="sm" colorScheme="gray" onClick={onBack}>
        {label}
      </Button>
    ) : null;

  if (step === "done") {
    return (
      <VStack spacing="12px" align="stretch">
        <Text fontSize="13px" color="gray.300" lineHeight="20px">
          Your password is reset. Log in with the new one — welcome back.
        </Text>
        {onBack && (
          <Button className="auth-submit" width="100%" onClick={onBack}>
            Back to Login →
          </Button>
        )}
      </VStack>
    );
  }

  if (step === "otp") {
    return (
      <VStack spacing="12px" align="stretch" as="form" onSubmit={verify}>
        <Text fontSize="13px" color="gray.300" lineHeight="20px">
          We sent a <b>6-digit reset code</b> to{" "}
          <b style={{ color: "#fff" }}>{email}</b>. Enter it below — it
          expires in 10 minutes.
        </Text>
        <FormControl isRequired>
          <FormLabel className="auth-label">Reset code</FormLabel>
          <Input
            ref={otpRef}
            className="auth-input"
            value={otp}
            onChange={(e) =>
              setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            placeholder="••••••"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            textAlign="center"
            letterSpacing="8px"
            fontSize="22px"
            fontWeight="800"
          />
        </FormControl>
        {preview && (
          <Text
            fontSize="12px"
            color="yellow.200"
            bg="whiteAlpha.100"
            borderRadius="8px"
            p="8px 12px"
          >
            Dev preview (no SMTP configured): <b>{preview}</b>
          </Text>
        )}
        <Button
          className="auth-submit"
          width="100%"
          style={{ marginTop: 8 }}
          type="submit"
          isLoading={loading}
          loadingText="Verifying…"
        >
          Verify Code →
        </Button>
        <HStack justify="space-between" align="center" pt="2px">
          <Button
            variant="link"
            size="sm"
            colorScheme="cyan"
            onClick={resend}
            isLoading={resending}
            isDisabled={cooldown > 0}
          >
            {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
          </Button>
          <Button
            variant="link"
            size="sm"
            colorScheme="gray"
            onClick={() => setStep("email")}
          >
            ← Use a different email
          </Button>
        </HStack>
        <HStack justify="center" pt="2px">
          {backLink("← Back to login")}
        </HStack>
      </VStack>
    );
  }

  if (step === "password") {
    return (
      <VStack spacing="12px" align="stretch" as="form" onSubmit={reset}>
        <Text fontSize="13px" color="gray.300" lineHeight="20px">
          Code accepted for <b style={{ color: "#fff" }}>{email}</b>. Choose
          a new password (min 6 characters).
        </Text>
        <FormControl isRequired>
          <FormLabel className="auth-label">New password</FormLabel>
          <InputGroup size="md" className="auth-input-wrap">
            <Input
              className="auth-input"
              type={showPw ? "text" : "password"}
              placeholder="Make it unbreakable"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              pr="4.8rem"
            />
            <InputRightElement width="4.5rem" h="100%">
              <Button
                className="show-btn"
                h="1.75rem"
                size="sm"
                onClick={() => setShowPw((v) => !v)}
              >
                {showPw ? "Hide" : "Show"}
              </Button>
            </InputRightElement>
          </InputGroup>
          {newPassword && (
            <>
              <div className="strength-track">
                <div
                  className="strength-fill"
                  style={{ width: `${strength.pct}%`, background: strength.color }}
                />
              </div>
              <div className="strength-label" style={{ color: strength.color }}>
                {strength.label}
              </div>
            </>
          )}
        </FormControl>
        <FormControl isRequired>
          <FormLabel className="auth-label">Confirm new password</FormLabel>
          <Input
            className="auth-input"
            type={showPw ? "text" : "password"}
            placeholder="One more time"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            borderColor={
              matchState
                ? matchState.ok
                  ? "green.400 !important"
                  : "red.400 !important"
                : undefined
            }
          />
          {matchState && (
            <Text
              fontSize="11px"
              fontWeight="700"
              mt="6px"
              color={matchState.ok ? "green.300" : "red.300"}
            >
              {matchState.msg}
            </Text>
          )}
        </FormControl>
        <Button
          className="auth-submit"
          width="100%"
          style={{ marginTop: 8 }}
          type="submit"
          isLoading={loading}
          loadingText="Resetting…"
        >
          Set New Password →
        </Button>
        <HStack justify="center" pt="2px">
          {backLink("← Back to login")}
        </HStack>
      </VStack>
    );
  }

  return (
    <VStack spacing="12px" align="stretch" as="form" onSubmit={sendCode}>
      <Text fontSize="13px" color="gray.300" lineHeight="20px">
        Enter your account email — we&apos;ll send a{" "}
        <b>6-digit reset code</b> (expires in 10 minutes).
      </Text>
      <FormControl id="reset-email" isRequired>
        <FormLabel className="auth-label">Email Address</FormLabel>
        <Input
          className="auth-input"
          value={email}
          type="email"
          placeholder="you@electric.com"
          onChange={(e) => setEmail(e.target.value)}
        />
      </FormControl>
      <Button
        className="auth-submit"
        width="100%"
        style={{ marginTop: 8 }}
        type="submit"
        isLoading={loading}
        loadingText="Sending…"
      >
        Send Reset Code →
      </Button>
      <HStack justify="center" pt="2px">
        {backLink("← Back to login")}
      </HStack>
    </VStack>
  );
};

export default ForgotPassword;
